import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { QuestionTypesEnum, SpecialOptionQuestionTypesEnum } from '@hunch/frontend-shared/enums';
import { uniqBy } from 'lodash';
import { BehaviorSubject, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { UriConfig } from '@app/app.config';
import { EventDetailsModel } from '@app/common/models/EventDetailsModel';
import { SKIP_LOADING } from '@app/core/interseptors/skip-loading.token';
import { APP_CONFIG } from '@app/general.app.config';
import { StreakQuestionListItemModel } from '@models/_streak/StreakQuestionListItemModel';
import { ApiCorrectAnswerItem } from '@models/CorreectAnswerDetailsModel';
import { LocaleItem, QuestionLocaleItem } from '@models/LocalesResponseModel';
import { QuestionModel } from '@models/QuestionModel';
import { withMappedCorrectAnswers } from '@modules/mappers/correct-answer-api-mapper';

@Injectable({
  providedIn: 'root'
})
export class QuestionsBankService {
  protected http = inject(HttpClient);
  private uriConfig = inject(UriConfig);

  needUpdateQuestions$ = new BehaviorSubject(true);

  questionsBank = signal<(QuestionLocaleItem | { [key: string]: string })[]>([]);

  externalAnswerOptions = ['AWAY_TEAM', 'HOME_TEAM'];

  externalAnswerOptionsForCreation = ['AWAY_TEAM', 'HOME_TEAM', 'DRAW'];

  total$ = new BehaviorSubject(0);

  cashedQuestions = new Map<number | string, { [key: string]: string }[] | number>();

  private pendingQuestionListRequests = new Map<
    string,
    Observable<{ records: { [key: string]: string }[]; total: number }>
  >();

  createQuestion(question, questionType, localeizationData, locales, answerGroups, isStreak = false, hardcodedIds) {
    const questionBody = this.createQuestionBody(
      question,
      questionType,
      localeizationData,
      locales,
      answerGroups,
      isStreak,
      [...hardcodedIds]
    );
    if (isStreak) {
      return this.http.post<QuestionModel>(this.uriConfig.questionsStreak, questionBody);
    } else {
      return this.http.post<QuestionModel>(this.uriConfig.questions, questionBody);
    }
  }

  updateQuestion(question, questionType, localeizationData, locales, answerGroups, id, isStreak = false, hardcodedIds) {
    const questionBody = this.createQuestionBody(
      question,
      questionType,
      localeizationData,
      locales,
      answerGroups,
      isStreak,
      [...hardcodedIds]
    );
    if (isStreak) {
      return this.http.patch<QuestionModel>(this.uriConfig.questions + '/' + id + '/streak', questionBody);
    } else {
      return this.http.patch<QuestionModel>(this.uriConfig.questions + '/' + id, questionBody);
    }
  }

  getQuestionById(id: number, isStreak = false): Observable<QuestionModel> {
    return this.http
      .get<
        Omit<QuestionModel, 'correctAnswers'> & { correctAnswers?: ApiCorrectAnswerItem[] }
      >(this.uriConfig.questions + '/' + id + (isStreak ? '/streak' : ''))
      .pipe(map(withMappedCorrectAnswers));
  }

  getLocalizations(localizationData, locales, answersKey?, index?) {
    return Object.keys(localizationData).map(locale => {
      return {
        localeId: locales.find(item => item.country === locale).id,
        text: answersKey ? localizationData[locale][answersKey][index].text : localizationData[locale].text
      };
    });
  }

  getLocalizedAnswers(groupId, answers, localeizationData, locales, answerGroups, isStreak, hardcodedIds) {
    const groupName = answerGroups.find(item => item.id === groupId);
    return answers.map((answer, index) => {
      const localizations = hardcodedIds.includes(answer.id)
        ? []
        : this.getLocalizations(localeizationData, locales, 'answers', index);
      const resultAnswer = {
        answerId: answer.id,
        groupName: groupName ? groupName.name : null,
        imageUrl: answer?.imageUrl || null,
        localizations
      };
      if (!isStreak) {
        resultAnswer['answerGroupId'] = groupId ? groupId : null;
      }
      return resultAnswer;
    });
  }

  getLocalizedAnswersForLists(
    groupId1,
    groupId2,
    answers1st,
    answers2st,
    localeizationData,
    locales,
    answerGroups,
    isStreak
  ) {
    const groupName1 = answerGroups.find(item => item.id === groupId1);
    const groupName2 = answerGroups.find(item => item.id === groupId2);

    const answers1 = answers1st.map((answer, index) => {
      const resultAnswer = {
        answerId: answer.id,
        groupName: groupName1 ? groupName1.name : 'group_0',
        imageUrl: answer?.imageUrl || null,
        localizations: this.getLocalizations(localeizationData, locales, 'answers1st', index)
      };
      if (!isStreak && groupId1) {
        resultAnswer['answerGroupId'] = groupId1;
      }
      return resultAnswer;
    });

    const answers2 = answers2st.map((answer, index) => {
      const resultAnswer = {
        answerId: answer.id,
        groupName: groupName2 ? groupName2.name : 'group_1',
        imageUrl: answer?.imageUrl || null,
        localizations: this.getLocalizations(localeizationData, locales, 'answers2st', index)
      };

      if (!isStreak && groupId2) {
        resultAnswer['answerGroupId'] = groupId2;
      }

      return resultAnswer;
    });

    return [...answers1, ...answers2];
  }

  createQuestionBody(question, questionType, localeizationData, locales, answerGroups, isStreak, hardcodedIds = []) {
    const questionBody = this.buildBaseQuestionBody(question, localeizationData, locales);

    if (!isStreak) {
      this.applyPredictionBankFields(questionBody, questionType, question.type);
    }

    return this.applyTypeSpecificFields(questionBody, questionType, {
      localeizationData,
      locales,
      answerGroups,
      isStreak,
      hardcodedIds
    });
  }

  private buildBaseQuestionBody(question, localeizationData, locales): Partial<QuestionModel> {
    const { type, text, imageUrl, statsKey, period, operator, target, sportType, optaCovered } = question;

    const questionBody: Partial<QuestionModel> = {
      type,
      text,
      sportType: sportType,
      imageUrl: imageUrl || null,
      difficultyLevel: question.difficultyLevel,
      localizations: this.getLocalizations(localeizationData, locales)
    };

    if (statsKey) {
      questionBody.autoResultingData = { statsKey, period, operator, ...(target && { target }) };
      questionBody.optaCovered = optaCovered;
    }

    if (!operator && !period && !statsKey) {
      questionBody.autoResultingData = null;
    }

    if (question.correctAnswers !== undefined) {
      questionBody.correctAnswers = question.correctAnswers;
    }

    if (question.correctAnswerValue !== undefined) {
      questionBody.correctAnswerValue = question.correctAnswerValue;
    }

    if (question.voidType !== undefined) {
      questionBody.voidType = question.voidType;
    }

    return questionBody;
  }

  private applyPredictionBankFields(questionBody: Partial<QuestionModel>, questionType, type: QuestionTypesEnum): void {
    questionBody.isForPredictionBank = true;
    delete questionBody.difficultyLevel;

    if (
      questionType?.groupId &&
      (type === QuestionTypesEnum.LIST || type === QuestionTypesEnum.GRID || type === QuestionTypesEnum.OPTIONS)
    ) {
      questionBody.answerGroupId = questionType.groupId;
    }

    if (type === QuestionTypesEnum.LISTS && questionType) {
      questionBody.answerGroupId = questionType.answerGroupId ? questionType.answerGroupId : null;
      questionBody.answerGroupId2 = questionType.answerGroupId2 ? questionType.answerGroupId2 : null;
    }
  }

  private applyTypeSpecificFields(
    questionBody: Partial<QuestionModel>,
    questionType,
    context: { localeizationData; locales; answerGroups; isStreak; hardcodedIds }
  ): Partial<QuestionModel> {
    const { localeizationData, locales, answerGroups, isStreak, hardcodedIds } = context;

    switch (questionBody.type) {
      case QuestionTypesEnum.SCORE_PLUS:
        return {
          ...questionBody,
          maxScoreValue: questionType.maxScoreValue + '+'
        };
      case QuestionTypesEnum.SPECIAL_OPTIONS: {
        const { specialOption, overUnderValue } = questionType;
        questionBody.specialOption = specialOption;
        if (specialOption === SpecialOptionQuestionTypesEnum.OVER_UNDER) {
          questionBody.overUnderValue = +overUnderValue;
        }
        return questionBody;
      }
      case QuestionTypesEnum.RANGE:
        return {
          ...questionBody,
          attributes: {
            minValue: questionType.minValue,
            maxValue: questionType.maxValue,
            incrementValue: questionType.incrementValue
          }
        };
      case QuestionTypesEnum.OPTIONS:
      case QuestionTypesEnum.LIST:
      case QuestionTypesEnum.GRID:
        if (questionType?.additional) {
          return {
            ...questionBody,
            answers: this.getLocalizedAnswers(
              questionType.groupId,
              questionType.additional.answers,
              localeizationData,
              locales,
              answerGroups,
              isStreak,
              hardcodedIds
            )
          };
        }
        return questionBody;
      case QuestionTypesEnum.LISTS:
        if (questionType) {
          return {
            ...questionBody,
            answers: this.getLocalizedAnswersForLists(
              questionType.answerGroupId,
              questionType.answerGroupId2,
              questionType.additional.answers1st,
              questionType.additional.answers2st,
              localeizationData,
              locales,
              answerGroups,
              isStreak
            )
          };
        }
        return questionBody;
      default:
        return questionBody;
    }
  }

  fetchQuestionList(requestBody = {}, isStreak = false, enableCashing = false, skipLoading = false) {
    let params = new HttpParams();
    Object.keys(requestBody).forEach(param => {
      if (param === 'difficultyLevels') {
        requestBody[param]?.forEach(level => {
          params = params.append(param, level);
        });
      } else {
        params = params.set(param, requestBody[param]);
      }
    });

    const cacheKey = JSON.stringify({ requestBody, isStreak, skipLoading });
    const pendingRequest = this.pendingQuestionListRequests.get(cacheKey);

    if (enableCashing && pendingRequest) {
      return pendingRequest;
    }

    if (enableCashing && !requestBody['text'] && this.cashedQuestions.get(requestBody['pageNumber'])) {
      return of({
        records: this.cashedQuestions.get(requestBody['pageNumber']) as { [key: string]: string }[],
        total: this.cashedQuestions.get('total') as number
      });
    } else {
      const request$ = this.http
        .get<{
          records: { [key: string]: string }[];
          total: number;
        }>(this.uriConfig.baseApiUrl + 'questions' + (isStreak ? '/streak' : '/prediction'), {
          params,
          context: new HttpContext().set(SKIP_LOADING, skipLoading)
        })
        .pipe(
          tap(questions => {
            this.questionsBank.set(uniqBy([...this.questionsBank(), ...questions.records], 'id'));
            if (enableCashing && !requestBody['text']) {
              if (requestBody['pageNumber'] === 1) {
                this.clearCashedQuestions();
                this.cashedQuestions.set('total', questions.total);
              }
              this.cashedQuestions.set(requestBody['pageNumber'], questions.records);
            }
          }),
          finalize(() => this.pendingQuestionListRequests.delete(cacheKey)),
          shareReplay(1)
        );

      if (enableCashing) {
        this.pendingQuestionListRequests.set(cacheKey, request$);
      }

      return request$;
    }
  }

  clearCashedQuestions() {
    this.cashedQuestions.clear();
    this.pendingQuestionListRequests.clear();
  }

  deleteQuestion(questionId) {
    return this.http.delete(this.uriConfig.questions + '/' + questionId);
  }

  fetchQuestionListByRoundId(roundId): Observable<StreakQuestionListItemModel[]> {
    return this.http
      .get<
        (Omit<StreakQuestionListItemModel, 'correctAnswers'> & { correctAnswers?: ApiCorrectAnswerItem[] })[]
      >(this.uriConfig.questionsStreak + '/' + roundId)
      .pipe(map(questions => questions.map(withMappedCorrectAnswers)));
  }

  toggleQuestionStatus(questionId: number, isDisabled: boolean) {
    return this.http.patch<QuestionModel>(`${this.uriConfig.questions}/${questionId}/streak`, { isDisabled });
  }

  getQuestionTypeLabel(type: QuestionTypesEnum) {
    switch (type) {
      case QuestionTypesEnum.SCORE:
        return 'Correct score format';
      case QuestionTypesEnum.SCORE_PLUS:
        return 'Custom Score';
      case QuestionTypesEnum.RANGE:
        return 'Select number from slider';
      case QuestionTypesEnum.OPTIONS:
        return 'Select from 2 answers option';
      case QuestionTypesEnum.LIST:
        return '1 from 1 list';
      case QuestionTypesEnum.LISTS:
        return '1 from 2 list';
      case QuestionTypesEnum.GRID:
        return '1 from grid';
      case QuestionTypesEnum.SPECIAL_OPTIONS:
        return 'Special Options';
      default:
        return 'Correct score format';
    }
  }

  transformQuestionForSelect(question): string {
    return `ID: ${question.id} Text: ${question.text} Type: ${this.getQuestionTypeLabel(question.type)}`;
  }

  transformLocalizations(localizations: LocaleItem[], locales, sportEvent?: EventDetailsModel) {
    return localizations.map(locale => {
      let text = locale?.text;

      if (text && sportEvent) {
        const hasMatch = APP_CONFIG.EXTERNAL_ANSWER_OPTIONS.some(option => text?.includes(option));
        if (hasMatch) {
          const {
            awayTeamSportEvents: { name: awayTeamName },
            homeTeamSportEvents: { name: homeTeamName }
          } = sportEvent;
          const [awayTeam, homeTeam] = APP_CONFIG.EXTERNAL_ANSWER_OPTIONS;

          text = text.replace(homeTeam, homeTeamName);
          text = text.replace(awayTeam, awayTeamName);
        }
      }

      return {
        text: text,
        localeId: locales.find(item => item.i18n === locale?.locale?.i18n).id
      };
    });
  }

  isListQuestionType(type: QuestionTypesEnum) {
    return (
      type === QuestionTypesEnum.LISTS ||
      type === QuestionTypesEnum.OPTIONS ||
      type === QuestionTypesEnum.GRID ||
      type === QuestionTypesEnum.LIST
    );
  }
}
